import React, { useEffect, useState } from "react";
import "./styles.scss";
import JoinPosterService from "../../../api/services/JoinPoster";
import JoinPosterAssortmentContent from "./JoinPosterAssortmentContent";
import JoinPosterAssortmentNavLeft from "./components/JoinPosterAssortmentNavLeft";
import { showCommonError } from "../../../tools/common/additionalFuncs";
import {
  normalizeCategories,
  normalizeProductsResponse,
  updateCategoryVisibility,
  updateProductsVisibility,
} from "./utils";

const JoinPosterAssortment = ({ merchantId }) => {
  const [shops, setShops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [productsData, setProductsData] = useState({ results: [] });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!merchantId) return;
    loadShops(merchantId);
  }, [merchantId]);

  const loadShops = async (id) => {
    setIsLoading(true);
    setSelectedShop(null);
    setCategories([]);
    setSelectedCategoryId(null);
    setProductsData({ results: [] });
    try {
      const shopsResp = await JoinPosterService.getShops(id);
      const results = shopsResp?.data?.results || [];
      const options = results.map((shop) => ({
        name: shop.name,
        value: shop.id,
        shop,
      }));
      setShops(options);
    } catch (e) {
      showCommonError();
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeShop = async (item) => {
    if (!merchantId || !item) return false;
    if (selectedShop && `${selectedShop.value}` === `${item.value}`) {
      return true;
    }
    setIsLoading(true);
    try {
      const categoriesResp = await JoinPosterService.getCategories({
        merchantId,
        shopRefId: item.value,
      });
      const results = categoriesResp?.data?.results || [];
      const sortedCategories = normalizeCategories(results);
      setCategories(sortedCategories);
      const firstCategoryId = sortedCategories[0]?.id ?? null;
      setSelectedCategoryId(firstCategoryId);
      setSelectedShop(item);
      if (firstCategoryId) {
        const productsResp = await JoinPosterService.getProductsByCategory({
          merchantId,
          shopRefId: item.value,
          categoryRefId: firstCategoryId,
        });
        setProductsData(
          normalizeProductsResponse(productsResp?.data),
        );
      } else {
        setProductsData({ results: [] });
      }
      return true;
    } catch (e) {
      showCommonError();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const onToggleCategory = async (item, nextValue) => {
    if (!merchantId || !selectedShop || !item) {
      return false;
    }
    try {
      const resp = await JoinPosterService.changeCategoryVisibility({
        merchantId,
        shopRefId: selectedShop.value,
        categoryRefId: item.id,
        visibility: !!nextValue,
      });
      if (resp && resp.status && resp.status === 200) {
        setCategories((prev) =>
          updateCategoryVisibility(prev, item.id, nextValue),
        );
        return true;
      }
      showCommonError();
      return false;
    } catch (e) {
      showCommonError();
      return false;
    }
  };

  const onToggleProduct = async (product, nextValue) => {
    if (!merchantId || !selectedShop || !product) return false;
    if (!selectedCategoryId) {
      showCommonError();
      return false;
    }
    try {
      const resp = await JoinPosterService.changeProductVisibility({
        merchantId,
        shopRefId: selectedShop.value,
        categoryRefId: selectedCategoryId,
        productId: product.id,
        visibility: !!nextValue,
      });
      if (resp?.status === 200) {
        setProductsData((prev) =>
          updateProductsVisibility(prev, product.id, nextValue),
        );
        return true;
      }
      showCommonError();
      return false;
    } catch (e) {
      showCommonError();
      return false;
    }
  };

  const onSelectCategory = async (category) => {
    if (!merchantId || !selectedShop || !category) return false;
    if (`${selectedCategoryId}` === `${category.id}`) return true;
    setIsLoading(true);
    try {
      const productsResp = await JoinPosterService.getProductsByCategory({
        merchantId,
        shopRefId: selectedShop.value,
        categoryRefId: category.id,
      });
      setSelectedCategoryId(category.id);
      setProductsData(normalizeProductsResponse(productsResp?.data));
      return true;
    } catch (e) {
      showCommonError();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="JoinPosterAssortmentWrap">
      <JoinPosterAssortmentNavLeft
        shops={shops}
        categories={categories}
        selectedShopId={selectedShop ? selectedShop.value : null}
        selectedCategoryId={selectedCategoryId}
        onChangeShop={onChangeShop}
        onToggleCategory={onToggleCategory}
        onSelectCategory={onSelectCategory}
      />
      {!isLoading && (
        <JoinPosterAssortmentContent
          productsData={productsData}
          onToggleProduct={onToggleProduct}
        />
      )}
    </div>
  );
};

export default JoinPosterAssortment;
